import React, { useCallback } from 'react'
import Input from './Input'
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputProps, MaskValue } from './types'
import { TextInputProps } from 'react-native'
import { tlIfExists } from '@ui/utils/i18n'

export type BaseFormInputProps<T extends FieldValues = FieldValues> = {
  name: Path<T>
  control: Control<T>
  validate?: (value: string) => boolean
  refine?: (value: string) => string
  mask?: MaskValue
} & InputProps

export type FormInputWithContextProps<T extends FieldValues = FieldValues> = {
  next?: Path<T>
} & Omit<BaseFormInputProps<T>, 'control'> &
  InputProps

const BaseFormInput = <T extends FieldValues = FieldValues>({
  name,
  control,
  validate,
  refine,
  onBlur: onBlurProp,
  ...props
}: BaseFormInputProps<T>) => {
  const { field, fieldState } = useController({
    name,
    control,
  })

  const { error } = fieldState

  const { i18n } = useTranslation(['validation'])

  const onChangeText = useCallback(
    (text: string) => {
      if (validate && !validate(text)) {
        return
      }
      const refined = refine ? refine(text) : text
      field.onChange(refined)
    },
    [field, validate, refine]
  )

  const { ref, value, onBlur } = field

  const onBlurComposed = useCallback<NonNullable<TextInputProps['onBlur']>>(
    (e) => {
      onBlur()
      onBlurProp?.(e)
    },
    [onBlur, onBlurProp]
  )

  return (
    <Input
      {...props}
      value={typeof value === 'string' ? value : (value?.toString?.() ?? '')}
      ref={ref}
      onChangeText={onChangeText}
      onBlur={onBlurComposed}
      errorMessage={
        // TODO: Support structured validation errors with i18n params (for example maxLength with { max })
        // instead of plain key-only messages propagated from Zod.
        error?.message
          ? tlIfExists(i18n, `validation:${error.message}`, error.message)
          : props.errorMessage
      }
    />
  )
}

export const FormInputWithContext = <T extends FieldValues = FieldValues>({
  next,
  ...props
}: FormInputWithContextProps<T>) => {
  const { setFocus, control } = useFormContext<T>()

  const onSubmitEditing = useCallback(() => {
    if (next) {
      setFocus(next)
    }
  }, [next, setFocus])

  const enterKeyHint = next
    ? 'next'
    : props.onSubmitEditing
      ? 'done'
      : undefined

  return (
    <BaseFormInput
      // Allow those props to be overridden
      onSubmitEditing={next ? onSubmitEditing : undefined}
      enterKeyHint={enterKeyHint}
      {...props}
      control={control}
    />
  )
}

export type FormInputProps<T extends FieldValues> = {
  control?: Control<T>
} & FormInputWithContextProps<T>

const FormInput = <T extends FieldValues>(props: FormInputProps<T>) => {
  const { control } = props

  if (!control || props.next) {
    return <FormInputWithContext {...props} />
  }

  return <BaseFormInput {...props} control={control} />
}

export default FormInput
